import { Component, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { ModalController, Platform } from '@ionic/angular';

import { EntraService } from '../../service/entra.service';
import { PwaService } from '../../service/pwa.service';
import { CameraTestComponent } from '../shared-components/camera-test/camera-test.component';
import { PrinterTestComponent } from '../shared-components/printer-test/printer-test.component';
import { environment } from '../../../environments/environment';
import { SHARED_FLOW_ROUTE } from '../../domain/receiving-log/rules/new-log-route.rules';


/**
 * LandingPageComponent
 * 
 * This component is responsible for displaying the landing page of the app. 
 * It manages user login state and navigating to different parts of the application.
 *
 * Subscriptions and behavior:
 * - Subscribes to `userIsLoggedIn` from the EntraService to monitor the user's login state.
 * - Navigates to different routes based on user actions.
 */

@Component({
  selector: 'app-landing-page',
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.css'],
})
export class LandingPageComponent  implements OnInit, OnDestroy {
public _entraService: EntraService
public logInSubscription!: Subscription;
public userIsLoggedIn: boolean = false;
public userHasAccess: boolean = false;
/** True on a phone (iOS/Android) — drives the streamlined mobile landing layout. */
public isMobile: boolean = false;
/** Testing tools (camera/printer test) are only exposed outside production. */
public showTestingTools: boolean = !environment.production;
/**
 * Whether to offer the "add to your home screen" prompt. False once the app is
 * running from its installed icon, and false anywhere the steps would not
 * describe what the user is looking at — desktop, or an iOS browser with no
 * share sheet. Read once at construction: neither can change without a reload.
 */
public canInstallApp: boolean = false;
/** Whether the shared install-instructions modal is presented. */
public showInstallHelp: boolean = false;

private _router: Router
private _modalController: ModalController



  


  private _platform: Platform

  constructor( entraService: EntraService, router: Router,
    public modalController: ModalController,
    platform: Platform,
    pwaService: PwaService
  ) {
    this.canInstallApp = pwaService.canAddToHomeScreen;
    this._entraService = entraService;
    this._router = router;
    this._modalController = modalController;
    this._platform = platform;
    this.isMobile = this._platform.is('ios') || this._platform.is('android');

    const userHasAccess = JSON.parse(localStorage.getItem('userHasAccess') || 'false');
    this.userHasAccess = userHasAccess;

  
  }


  /**
 * ngOnInit
 * 
 * Initializes the component by subscribing to the `userIsLoggedIn` behavior subject from the EntraService.
 * This allows tracking of the login state and updates the `userIsLoggedIn` and 'userHasAccess' properties accordingly.
 */

  ngOnInit(): void {
    if (this.logInSubscription) this.logInSubscription
    this.logInSubscription = this._entraService.userIsLoggedIn.subscribe((value) => {
      this.userIsLoggedIn = value;
      const userHasAccess = JSON.parse(localStorage.getItem('userHasAccess') || 'false');
      this.userHasAccess = userHasAccess;
    });


  }


  /**
 * ngOnDestroy
 * 
 * Unsubscribes from the `userIsLoggedIn` subject when the component is destroyed to prevent memory leaks.
 */

  ngOnDestroy(): void {
    this.logInSubscription.unsubscribe();
  }


  /**
 * navigateTo
 * 
 * Navigates the user to a different route based on the provided `path`.
 * Uses the Angular Router to change views within the app.
 * 
 * Method is called when the user clicks on a button or link that requires navigation.
 * 
 * @param path - The destination route to navigate to.
 */

  public navigateTo(path: string): void {
    this._router.navigate([path]);

  }

  /**
   * onSubmitReceivingEntry
   *
   * Entry point for a new receiving log — the shared receiving flow, on every
   * device. The flow lays itself out for the surface it opens on.
   */
  public onSubmitReceivingEntry(): void {
    this._router.navigate([SHARED_FLOW_ROUTE]);
  }

  
/**
 * login
 * 
 * Logs the user into the application by removing any stored location and last URL from localStorage.
 * Initiates the login process via the EntraService.
 * 
 * Method is called when the user clicks on the login button.
 */

  public  login() {
      localStorage.removeItem('selectedLocation');
      localStorage.removeItem('lastUrl');
      this._entraService.login();
      }

  /**
   * openCameraTest
   * 
   * Opens the camera testing modal for testing camera and QR code scanning functionality.
   * This is for development/testing purposes only.
   */
  public async openCameraTest(): Promise<void> {
    const modal = await this._modalController.create({
      component: CameraTestComponent,
      cssClass: 'camera-test-modal',
    });
    await modal.present();
  }

  /**
   * openPrinterTest
   *
   * Opens the printer testing modal for sending test jobs to the print API with
   * different print-option combinations and inspecting the per-document results.
   * This is for development/testing purposes only.
   */
  public async openPrinterTest(): Promise<void> {
    const modal = await this._modalController.create({
      component: PrinterTestComponent,
      cssClass: 'printer-test-modal',
    });
    await modal.present();
  }
}